import { ComponentProps, useRef, useState } from "react";
import Slider from "react-slick";
import BrandclubVideo from "../../../BrandclubVideo";
import {
  ContentContainer,
  Section,
  VideosContent,
  H2,
  Img,
} from "./aboutStyles";
import { VideoDetails } from "./constants";
import { TextTransition } from "./TextTransition";
import { useMediaQuery, useTheme } from "@mui/material";

type SliderProps = ComponentProps<typeof Slider>;

interface VideosSectionProps {
  videos: VideoDetails[];
}

interface SliderArrowControlsProps {
  sliderRef: React.RefObject<Slider | null>;
}

const baseSliderProps: SliderProps = {
  arrows: false,
  infinite: true,
  centerMode: true,
  centerPadding: "0px",
  lazyLoad: "progressive", // used to prevent janky behavior
  focusOnSelect: false,
};

const desktopSliderProps: SliderProps = {
  ...baseSliderProps,
  slidesToShow: 3,
  slidesToScroll: 1,
  swipe: false,
  adaptiveHeight: true,
};

const mobileSliderProps: SliderProps = {
  ...baseSliderProps,
  slidesToShow: 1,
  slidesToScroll: 1,
  className: "mobile_video_slider",
  swipe: true,
};

interface VideoDescriptionProps {
  currentVideo: VideoDetails;
}

const VideoDescription = ({ currentVideo }: VideoDescriptionProps) => {
  return (
    <div className="videos_desc">
      <div className="videos_text">
        <TextTransition className="comment">
          {currentVideo.comment}
        </TextTransition>
      </div>

      <div className="videos_text">
        <TextTransition className="name">{currentVideo.name}</TextTransition>
      </div>
      <div className="videos_text">
        <TextTransition style={{ width: "100%" }} className="city">
          {currentVideo.city}
        </TextTransition>
      </div>
    </div>
  );
};

const SliderArrowControls = ({ sliderRef }: SliderArrowControlsProps) => (
  <>
    <button
      className={"videos_button maxleft"}
      onClick={() => {
        sliderRef?.current?.slickPrev();
      }}
      title="Previous"
    >
      <Img
        className="perks_icon"
        src="https://media.brandclub.com/brandclub/image_asset/arrow_left_blue.svg"
        alt="arrow_l"
      />
    </button>
    <button
      className={"videos_button maxright"}
      onClick={() => {
        sliderRef?.current?.slickNext();
      }}
      title="Next"
    >
      <Img
        className="perks_icon"
        src="https://media.brandclub.com/brandclub/image_asset/arrow_right_blue.svg"
        alt="arrow_l"
      />
    </button>
  </>
);

const VideosSection = ({ videos }: VideosSectionProps) => {
  const theme = useTheme();
  // used to avoid rendering both mobile and desktop sliders which causes janky behavior
  const isTabletView = useMediaQuery(theme.breakpoints.down("md"));
  const [activeIndex, SetActiveIndex] = useState(0);
  const sliderRef = useRef<Slider | null>(null);

  return (
    <Section className="grey_bg">
      <ContentContainer>
        <VideosContent>
          <div className="videos_container">
            <div className="header_container">
              <H2 className="videos_title">Why people love Brandclub</H2>
            </div>
            <div className={isTabletView ? "people_mobile" : "people"}>
              {!isTabletView && <SliderArrowControls sliderRef={sliderRef} />}
              <Slider
                ref={sliderRef}
                {...(isTabletView ? mobileSliderProps : desktopSliderProps)}
                beforeChange={(_, next) => SetActiveIndex(next)}
              >
                {videos.map((item, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <div key={idx}>
                      <div
                        style={isTabletView ? { padding: "0 8px" } : undefined}
                        className={`card ${isActive ? "select" : ""}`}
                      >
                        <BrandclubVideo
                          lowerPlaybutton
                          canPlay={isActive}
                          videoUrl={item.video}
                          posterUrl={item.posterUrl}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (idx === activeIndex) {
                              return;
                            }

                            const totalVideos = videos.length;
                            // Check if clicked video is immediately to the left of active video
                            const isClickedVideoOnLeft =
                              (idx + 1) % totalVideos === activeIndex;
                            // Check if clicked video is immediately to the right of active video
                            const isClickedVideoOnRight =
                              (activeIndex + 1) % totalVideos === idx;

                            if (isClickedVideoOnLeft) {
                              sliderRef?.current?.slickPrev();
                            } else if (isClickedVideoOnRight) {
                              sliderRef?.current?.slickNext();
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </Slider>
            </div>
            <VideoDescription currentVideo={videos[activeIndex]} />
          </div>
        </VideosContent>
      </ContentContainer>
    </Section>
  );
};

export default VideosSection;
